import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RootState, useAppDispatch, useAppSelector } from '@/redux/store';
import { OrderStatus } from '@/interfaces/Order/order.interface';
import { columnColors, formatRand } from '@/utility/helper';
import { Layout } from '@/components/Layout/Layout';
import { fetchLiveOrders } from '@/redux/features/UserSlice';
import { getNormalizedStatus } from '@/utility/orderHelper';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { MainHeading } from '@/components/common/MainHeading';

const STATUS_MAPPING: Record<string, string> = {
    'pending': 'PENDING',
    'accepted': 'ACCEPTED',
    'in_progress': 'PREPARING',
    'ready_for_pickup': 'READY',
    'driver_assigned': 'ASSIGNED',
    'picked_up': 'PICKED UP'
};

const BOARD_STATUSES: OrderStatus[] = ['pending', 'accepted', 'in_progress', 'ready_for_pickup', 'driver_assigned', 'picked_up'];

export default function LiveOrders() {
    const { orderList } = useAppSelector((state: RootState) => state.user);
    const dispatch = useAppDispatch();
    const roleNavigate = useRoleNavigate();

    const loadOrders = async () => {
        await dispatch(fetchLiveOrders()).unwrap();
    };

    useEffect(() => {
        // Initial API Load
        loadOrders();

        // 30-Second Interval for List Re-fetching (Polling as substitute for socket)
        const interval = setInterval(loadOrders, 60 * 1000);

        return () => {
            clearInterval(interval);
        };
    }, [dispatch]);

    const filteredOrders = orderList || [];

    const columns = BOARD_STATUSES.map(s => ({
        status: s,
        label: STATUS_MAPPING[s],
        orders: filteredOrders?.filter(o => getNormalizedStatus(o.status, o.driverId) === s),
    }));

    const handleOrderClick = (id: string) => {
        roleNavigate(RoutesLink.ViewOrders.replace(':id', id));
    };

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={"Live Oprations"}
                />
                <div className="flex overflow-x-auto gap-4 pb-6 scrollbar-hide">
                    {columns.map(col => (
                        <div key={col.status} className="flex-shrink-0 w-72 flex flex-col gap-4">
                            <div className={`bg-white rounded-t-lg border-t-4 shadow-sm p-3 flex items-center justify-between ${columnColors[col.status] || 'border-border'}`}>
                                <h3 className="text-xs font-black text-foreground tracking-widest">{col.label}</h3>
                                <span className="w-5 h-5 rounded-full bg-secondary/50 flex items-center justify-center text-[10px] font-bold text-foreground">
                                    {col.orders?.length || 0}
                                </span>
                            </div>

                            <div className="flex-1 space-y-4 min-h-[500px] border-x border-b border-border/20 rounded-b-lg p-2 bg-secondary/5">
                                <AnimatePresence mode='popLayout'>
                                    {col.orders?.map(o => (
                                        <motion.div
                                            key={o.id}
                                            layout
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            onClick={() => handleOrderClick(o.id)}
                                            className="bg-[#F8F9FA] rounded-lg border border-border/30 p-4 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-black text-destructive group-hover:text-primary transition-colors">
                                                    {o.orderId || 'ORD-XXX'}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground font-bold">
                                                    {new Date(o.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                                </span>
                                            </div>

                                            <div className="space-y-1 mb-4">
                                                <p className="text-sm font-black text-foreground">
                                                    {o.user?.fullName || "Guest User"}
                                                </p>
                                                <p className="text-[11px] text-muted-foreground font-medium line-clamp-1">
                                                    {o.items?.map(i => `${i.quantity}x ${i.product?.name || 'Item'}`).join(', ')}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between pt-1">
                                                <span className="text-sm font-black text-foreground">
                                                    {formatRand(Number(o.finalAmount || o.totalAmount))}
                                                </span>
                                                <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-600 text-[10px] font-black tracking-tighter uppercase">
                                                    {o.paymentMethod || 'COD'}
                                                </span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                                {(!col.orders || col.orders.length === 0) && (
                                    <div className="flex items-center justify-center py-20">
                                        <p className="text-[11px] font-bold text-muted-foreground opacity-50 uppercase tracking-widest text-center">No orders</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
}
